'use client';

import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';
import PlatformIcon, { CategoryIcon } from '@/components/ui/PlatformIcon';

const categories = [
  {
    group: 'Social Media',
    slug: 'social-media',
    items: [
      { name: 'Instagram', desc: 'Aged & monetized creators', slug: 'instagram', href: '/accounts/instagram' },
      { name: 'TikTok', desc: 'Viral niche accounts', slug: 'tiktok', href: '/accounts/tiktok' },
      { name: 'YouTube', desc: 'Monetized channels', slug: 'youtube', href: '/accounts/youtube' },
      { name: 'Twitter / X', desc: 'Verified & aged profiles', slug: 'twitter-x', href: '/accounts/twitter-x' },
      { name: 'Reddit', desc: 'Karma-aged accounts', slug: 'reddit', href: '/accounts/reddit' },
      { name: 'Discord', desc: 'Aged servers & accounts', slug: 'discord', href: '/accounts/discord' },
      { name: 'Facebook', desc: 'Business pages & profiles', slug: 'facebook', href: '/accounts/facebook' },
      { name: 'Twitch', desc: 'Affiliate & partner accounts', slug: 'twitch', href: '/accounts/twitch' },
    ],
  },
  {
    group: 'Gaming',
    slug: 'gaming',
    items: [
      { name: 'Valorant', desc: 'Ranked & rare skins', slug: 'valorant', href: '/accounts/valorant' },
      { name: 'Fortnite', desc: 'OG skins & rare items', slug: 'fortnite', href: '/accounts/fortnite' },
      { name: 'CS2 / CSGO', desc: 'High-rank accounts', slug: 'cs2-csgo', href: '/accounts/cs2-csgo' },
      { name: 'League of Legends', desc: 'Smurf & high-elo', slug: 'league-of-legends', href: '/accounts/league-of-legends' },
      { name: 'GTA V', desc: 'Modded & high-level', slug: 'gta', href: '/accounts/gta' },
      { name: 'Roblox', desc: 'Robux & rare items', slug: 'roblox', href: '/accounts/roblox' },
      { name: 'OSRS', desc: 'Maxed & staked accounts', slug: 'osrs', href: '/accounts/osrs' },
      { name: 'World of Warcraft', desc: 'Geared & high iLvl', slug: 'world-of-warcraft', href: '/accounts/world-of-warcraft' },
    ],
  },
  {
    group: 'Financial',
    slug: 'financial',
    items: [
      { name: 'PayPal', desc: 'Verified payment accounts', slug: 'paypal', href: '/accounts/paypal' },
      { name: 'Cash App', desc: 'Verified & aged accounts', slug: 'cash-app', href: '/accounts/cash-app' },
      { name: 'Coinbase', desc: 'Crypto exchange accounts', slug: 'coinbase', href: '/accounts/coinbase' },
      { name: 'Binance', desc: 'Verified trading accounts', slug: 'binance', href: '/accounts/binance' },
      { name: 'Stripe', desc: 'Business accounts', slug: 'stripe', href: '/accounts/stripe' },
      { name: 'Wise', desc: 'Multi-currency accounts', slug: 'wise', href: '/accounts/wise' },
      { name: 'Skrill', desc: 'Digital wallet accounts', slug: 'skrill', href: '/accounts/skrill' },
      { name: 'Revolut', desc: 'Verified fintech accounts', slug: 'revolut', href: '/accounts/revolut' },
    ],
  },
  {
    group: 'Advertising',
    slug: 'advertising-marketing',
    items: [
      { name: 'Google Ads', desc: 'Aged & high-spend accounts', slug: 'google-ads', href: '/accounts/google-ads' },
      { name: 'Bing Ads', desc: 'Verified ad accounts', slug: 'bing-ads', href: '/accounts/bing-ads' },
      { name: 'Facebook Ads', desc: 'BM & threshold accounts', slug: 'facebook-ads', href: '/accounts/facebook-ads' },
      { name: 'Facebook Threshold', desc: 'High-spend BM accounts', slug: 'facebook-threshold', href: '/accounts/facebook-threshold' },
      { name: 'TikTok Ads', desc: 'Ad accounts', slug: 'tiktok-ads', href: '/accounts/tiktok-ads' },
      { name: 'TikTok Shop', desc: 'Seller profiles', slug: 'tiktok-shop', href: '/accounts/tiktok-shop' },
      { name: 'Trustpilot', desc: 'Review platform accounts', slug: 'trustpilot', href: '/accounts/trustpilot' },
      { name: 'Tripadvisor', desc: 'Review accounts', slug: 'tripadvisor', href: '/accounts/tripadvisor' },
    ],
  },
];

interface SearchItem {
  name: string;
  desc: string;
  slug: string;
  href: string;
  group: string;
  groupSlug: string;
}

const allSearchItems: SearchItem[] = categories.flatMap((cat) =>
  cat.items.map((item) => ({ ...item, group: cat.group, groupSlug: cat.slug }))
);

export default function Header({ variant = 'transparent' }: { variant?: 'transparent' | 'solid' }) {
  const [scrolled, setScrolled] = useState(variant === 'solid');
  const [mobileOpen, setMobileOpen] = useState(false);
  const [megaOpen, setMegaOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [platformIcons, setPlatformIcons] = useState<Record<string, string>>({});

  useEffect(() => {
    fetch('/api/platform-icons').then((r) => r.ok ? r.json() : {}).then(setPlatformIcons).catch(() => {});
  }, []);
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  /* ── search logic ─────────────────────────────────────────────── */
  const q = searchQuery.trim().toLowerCase();
  const searchResults: SearchItem[] = q.length
    ? allSearchItems.filter(
        (item) =>
          item.name.toLowerCase().includes(q) ||
          item.desc.toLowerCase().includes(q) ||
          item.group.toLowerCase().includes(q)
      )
    : [];

  const groupedResults = searchResults.reduce<Record<string, SearchItem[]>>((acc, item) => {
    (acc[item.group] ??= []).push(item);
    return acc;
  }, {});

  /* ── scroll ───────────────────────────────────────────────────── */
  useEffect(() => {
    if (variant === 'solid') return;
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [variant]);

  /* ── keyboard ─────────────────────────────────────────────────── */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(true);
        setMegaOpen(false);
        setTimeout(() => searchInputRef.current?.focus(), 50);
      }
      if (e.key === 'Escape') {
        setSearchOpen(false);
        setSearchQuery('');
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  /* ── click outside search ─────────────────────────────────────── */
  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(e.target as Node)) {
        setSearchOpen(false);
        setSearchQuery('');
      }
    };
    if (searchOpen) document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [searchOpen]);

  /* ── focus on open ────────────────────────────────────────────── */
  useEffect(() => {
    if (searchOpen) setTimeout(() => searchInputRef.current?.focus(), 50);
  }, [searchOpen]);

  const closeSearch = () => { setSearchOpen(false); setSearchQuery(''); };

  const isSolid = scrolled || searchOpen;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isSolid ? 'bg-white/95 backdrop-blur-xl shadow-nav border-b border-border' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-16 md:h-20 gap-4">

          {/* Logo */}
          <Link href="/" className="flex items-center gap-2.5 flex-shrink-0">
            <AppLogo src="/assets/images/logo-image.png" size={32} />
            <span
              className={`font-display font-800 text-xl tracking-tight transition-colors ${isSolid ? 'text-primary' : 'text-white'}`}
              style={{ fontWeight: 800 }}
            >
              TopAccounts
            </span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1 flex-1">
            {/* Megamenu trigger */}
            <div
              className="megamenu-trigger relative"
              onMouseEnter={() => { setMegaOpen(true); }}
              onMouseLeave={() => setMegaOpen(false)}
            >
              <button
                className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  isSolid
                    ? 'text-foreground-muted hover:text-foreground hover:bg-background-alt'
                    : 'text-white/80 hover:text-white hover:bg-white/10'
                }`}
              >
                Categories
                <Icon name="ChevronDownIcon" size={14} className={`transition-transform ${megaOpen ? 'rotate-180' : ''}`} />
              </button>

              {/* ── Megamenu ──────────────────────────────────────────── */}
              <div
                className={`megamenu absolute top-full left-1/2 -translate-x-1/2 mt-2 w-[1100px] bg-white border border-border rounded-2xl shadow-[0_24px_64px_rgba(26,26,46,0.16)] overflow-hidden ${
                  megaOpen ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible translate-y-2'
                } transition-all duration-200`}
              >
                {/* Megamenu header bar */}
                <div className="flex items-center justify-between px-7 py-4 bg-background-alt/60 border-b border-border">
                  <div>
                    <p className="text-sm font-700 text-foreground" style={{ fontWeight: 700 }}>Browse All Accounts</p>
                    <p className="text-xs text-foreground-muted mt-0.5">2,400+ verified listings — escrow-protected</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-success rounded-full animate-pulse" />
                    <span className="section-label text-success">Live marketplace</span>
                  </div>
                </div>

                <div className="p-7">
                  {/* 4-column grid */}
                  <div className="grid grid-cols-4 gap-7">
                    {categories.map((cat) => (
                      <div key={cat.group} className="flex flex-col">
                        {/* Column heading */}
                        <Link
                          href={`/category/${cat.slug}`}
                          className="flex items-center gap-1.5 section-label mb-3.5 hover:text-accent transition-colors group/cat"
                        >
                          <CategoryIcon slug={cat.slug} size={12} />
                          {cat.group}
                          <Icon name="ArrowRightIcon" size={10} className="ml-auto opacity-0 group-hover/cat:opacity-100 transition-opacity" />
                        </Link>

                        {/* Items */}
                        <ul className="space-y-0.5 flex-1">
                          {cat.items.map((item) => (
                            <li key={item.name}>
                              <Link
                                href={item.href}
                                className="flex items-start gap-2.5 px-2.5 py-2 rounded-xl hover:bg-background-alt group/item transition-colors"
                              >
                                <span className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5 text-accent">
                                  <PlatformIcon slug={item.slug} size={15} iconUrl={platformIcons[item.slug]} />
                                </span>
                                <div>
                                  <p className="text-xs font-600 text-foreground group-hover/item:text-primary transition-colors leading-tight" style={{ fontWeight: 600 }}>
                                    {item.name}
                                  </p>
                                  <p className="text-2xs text-foreground-muted mt-0.5">{item.desc}</p>
                                </div>
                              </Link>
                            </li>
                          ))}
                        </ul>

                        {/* View all button */}
                        <Link
                          href={`/category/${cat.slug}`}
                          className="mt-4 flex items-center justify-center gap-1.5 py-2 text-2xs font-600 text-accent border border-accent/25 bg-accent/5 rounded-xl hover:bg-accent/10 hover:border-accent/50 transition-all"
                          style={{ fontWeight: 600 }}
                        >
                          View all {cat.group} <Icon name="ArrowRightIcon" size={10} />
                        </Link>
                      </div>
                    ))}
                  </div>

                  {/* Footer row — more categories */}
                  <div className="mt-6 pt-5 border-t border-border flex items-center justify-between gap-4">
                    <div className="flex flex-wrap gap-2">
                      {[
                        { label: 'E-commerce (Amazon · eBay)', href: '/category/ecommerce-seller' },
                        { label: 'Mail (Gmail · Outlook · Yahoo)', href: '/category/mail' },
                        { label: 'Media (Netflix · Disney+)', href: '/category/media' },
                        { label: 'Developer (GitHub · AWS)', href: '/category/developer-infrastructure' },
                        { label: 'WhatsApp · Telegram', href: '/accounts/whatsapp' },
                      ].map((tag) => (
                        <Link
                          key={tag.label}
                          href={tag.href}
                          className="text-2xs text-foreground-muted border border-border rounded-full px-3 py-1 hover:border-accent hover:text-accent transition-colors"
                        >
                          {tag.label}
                        </Link>
                      ))}
                    </div>
                    <Link
                      href="/categories"
                      className="flex-shrink-0 text-xs font-600 text-accent hover:text-accent-dark flex items-center gap-1 transition-colors"
                      style={{ fontWeight: 600 }}
                    >
                      Browse all categories <Icon name="ArrowRightIcon" size={12} />
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            {[
              { label: 'How It Works', href: '/#how-it-works' },
              { label: 'About', href: '/about' },
              { label: 'Blog', href: '/blog' },
              { label: 'Support', href: '/contact-support' },
            ].map((item) => (
              <Link
                key={item.label}
                href={item.href}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  isSolid
                    ? 'text-foreground-muted hover:text-foreground hover:bg-background-alt'
                    : 'text-white/80 hover:text-white hover:bg-white/10'
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>

          {/* Right side: Search + CTAs */}
          <div className="hidden lg:flex items-center gap-2 flex-shrink-0">

            {/* ── Live Search ──────────────────────────────────────────── */}
            <div ref={searchContainerRef} className="relative">
              {/* Search trigger button */}
              <button
                onClick={() => { setSearchOpen(!searchOpen); setMegaOpen(false); }}
                className={`flex items-center gap-2 pl-3 pr-3 py-2 rounded-xl border text-sm transition-all ${
                  searchOpen
                    ? 'border-accent/50 bg-accent/5 text-foreground shadow-sm'
                    : isSolid
                    ? 'border-border text-foreground-muted hover:text-foreground hover:bg-background-alt'
                    : 'border-white/20 text-white/70 hover:text-white hover:bg-white/10'
                }`}
              >
                <Icon name="MagnifyingGlassIcon" size={15} className="flex-shrink-0" />
                <span className={`text-xs whitespace-nowrap ${isSolid || searchOpen ? 'text-foreground-muted' : 'text-white/50'}`}>
                  Search accounts…
                </span>
                <span className={`text-2xs px-1.5 py-0.5 rounded border font-mono leading-none ${
                  isSolid || searchOpen ? 'border-border text-foreground-muted bg-background-alt' : 'border-white/20 text-white/40'
                }`}>
                  ⌘K
                </span>
              </button>

              {/* ── Search panel ──────────────────────────────────────── */}
              <div
                className={`absolute top-full right-0 mt-2 w-[500px] bg-white border border-border rounded-2xl shadow-[0_24px_64px_rgba(26,26,46,0.18)] overflow-hidden z-50 transition-all duration-200 ${
                  searchOpen ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible translate-y-2'
                }`}
              >
                {/* Search input row */}
                <div className="flex items-center gap-3 px-4 py-3.5 border-b border-border">
                  <Icon name="MagnifyingGlassIcon" size={16} className="text-foreground-muted flex-shrink-0" />
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search platforms, account types…"
                    className="flex-1 text-sm text-foreground placeholder:text-foreground-muted outline-none bg-transparent"
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery('')}
                      className="text-foreground-muted hover:text-foreground transition-colors flex-shrink-0"
                    >
                      <Icon name="XMarkIcon" size={14} />
                    </button>
                  )}
                </div>

                {/* Results area */}
                <div className="max-h-[420px] overflow-y-auto overscroll-contain">
                  {q.length === 0 ? (
                    /* Popular picks (empty state) */
                    <div className="p-4">
                      <p className="section-label text-foreground-muted mb-3 px-1">Popular platforms</p>
                      <div className="grid grid-cols-2 gap-1">
                        {allSearchItems.slice(0, 8).map((item) => (
                          <Link
                            key={item.slug}
                            href={item.href}
                            onClick={closeSearch}
                            className="flex items-center gap-2.5 px-2.5 py-2 rounded-xl hover:bg-background-alt transition-colors group/sr"
                          >
                            <span className="w-7 h-7 bg-primary/8 rounded-lg flex items-center justify-center flex-shrink-0 group-hover/sr:bg-accent/10 transition-colors">
                              <PlatformIcon slug={item.slug} size={15} className="text-primary group-hover/sr:text-accent transition-colors" iconUrl={platformIcons[item.slug]} />
                            </span>
                            <div className="min-w-0">
                              <p className="text-xs font-600 text-foreground leading-tight" style={{ fontWeight: 600 }}>{item.name}</p>
                              <p className="text-2xs text-foreground-muted truncate">{item.group}</p>
                            </div>
                          </Link>
                        ))}
                      </div>
                      <div className="mt-3 pt-3 border-t border-border">
                        <Link
                          href="/categories"
                          onClick={closeSearch}
                          className="flex items-center justify-center gap-1.5 py-2 text-xs font-600 text-accent hover:text-accent-dark transition-colors"
                          style={{ fontWeight: 600 }}
                        >
                          Browse all categories <Icon name="ArrowRightIcon" size={12} />
                        </Link>
                      </div>
                    </div>
                  ) : searchResults.length === 0 ? (
                    /* No results */
                    <div className="py-10 text-center px-6">
                      <div className="w-10 h-10 bg-background-alt rounded-xl flex items-center justify-center mx-auto mb-3">
                        <Icon name="MagnifyingGlassIcon" size={18} className="text-foreground-muted" />
                      </div>
                      <p className="text-sm font-600 text-foreground mb-1" style={{ fontWeight: 600 }}>
                        No results for &ldquo;{searchQuery}&rdquo;
                      </p>
                      <p className="text-xs text-foreground-muted">Try a platform name like &ldquo;Instagram&rdquo; or &ldquo;Valorant&rdquo;</p>
                    </div>
                  ) : (
                    /* Grouped results */
                    <div className="p-3">
                      <p className="section-label text-foreground-muted px-2 mb-2">
                        {searchResults.length} result{searchResults.length !== 1 ? 's' : ''}
                      </p>
                      {Object.entries(groupedResults).map(([group, items]) => (
                        <div key={group} className="mb-2">
                          <p className="section-label text-foreground-muted/70 px-2 mb-1">{group}</p>
                          {items.map((item) => (
                            <Link
                              key={item.slug}
                              href={item.href}
                              onClick={closeSearch}
                              className="flex items-center gap-3 px-2.5 py-2.5 rounded-xl hover:bg-background-alt transition-colors group/sr"
                            >
                              <span className="w-8 h-8 bg-primary/8 rounded-xl flex items-center justify-center flex-shrink-0 group-hover/sr:bg-accent/10 transition-colors">
                                <PlatformIcon slug={item.slug} size={17} className="text-primary group-hover/sr:text-accent transition-colors" iconUrl={platformIcons[item.slug]} />
                              </span>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-600 text-foreground group-hover/sr:text-primary transition-colors" style={{ fontWeight: 600 }}>
                                  {item.name}
                                </p>
                                <p className="text-xs text-foreground-muted truncate">{item.desc}</p>
                              </div>
                              <Icon name="ArrowRightIcon" size={12} className="text-foreground-muted opacity-0 group-hover/sr:opacity-100 transition-opacity flex-shrink-0" />
                            </Link>
                          ))}
                        </div>
                      ))}
                      <div className="mt-1 pt-2 border-t border-border">
                        <Link
                          href="/categories"
                          onClick={closeSearch}
                          className="flex items-center justify-center gap-1.5 py-2 text-xs font-600 text-accent hover:text-accent-dark transition-colors"
                          style={{ fontWeight: 600 }}
                        >
                          Browse all categories <Icon name="ArrowRightIcon" size={12} />
                        </Link>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <Link
              href="/sign-in"
              className={`px-5 py-2.5 text-sm font-600 rounded-xl border transition-all ${
                isSolid
                  ? 'border-border text-foreground hover:bg-background-alt'
                  : 'border-white/30 text-white hover:bg-white/10'
              }`}
              style={{ fontWeight: 600 }}
            >
              Sign In
            </Link>
            <Link
              href="/sign-up"
              className="px-5 py-2.5 text-sm font-700 rounded-xl bg-accent text-primary hover:bg-accent-dark transition-all shadow-gold"
              style={{ fontWeight: 700 }}
            >
              Sign Up
            </Link>
          </div>

          {/* Mobile hamburger */}
          <button
            className={`lg:hidden p-2 rounded-lg transition-colors ${isSolid ? 'text-foreground' : 'text-white'}`}
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
          >
            <Icon name={mobileOpen ? 'XMarkIcon' : 'Bars3Icon'} size={22} />
          </button>
        </div>

        {/* ── Mobile Nav ──────────────────────────────────────────────── */}
        <div
          className={`lg:hidden overflow-hidden transition-all duration-300 ${
            mobileOpen ? 'max-h-screen opacity-100 pb-4' : 'max-h-0 opacity-0'
          } bg-white rounded-2xl mt-2 border border-border shadow-nav`}
        >
          <div className="p-4 space-y-1">
            {/* Mobile search */}
            <div className="relative mb-2">
              <Icon name="MagnifyingGlassIcon" size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-muted pointer-events-none" />
              <input
                type="text"
                placeholder="Search accounts…"
                className="w-full pl-9 pr-4 py-2.5 text-sm bg-background-alt border border-border rounded-xl outline-none focus:border-accent/50 transition-colors"
              />
            </div>
            <Link
              href="/categories"
              className="block px-4 py-3 text-sm font-600 text-foreground hover:bg-background-alt rounded-xl transition-colors"
              style={{ fontWeight: 600 }}
              onClick={() => setMobileOpen(false)}
            >
              Browse Categories
            </Link>
            {[
              { label: 'How It Works', href: '/#how-it-works' },
              { label: 'About', href: '/about' },
              { label: 'Blog', href: '/blog' },
              { label: 'Support', href: '/contact-support' },
            ].map((item) => (
              <Link
                key={item.label}
                href={item.href}
                className="block px-4 py-3 text-sm text-foreground-muted hover:text-foreground hover:bg-background-alt rounded-xl transition-colors"
                onClick={() => setMobileOpen(false)}
              >
                {item.label}
              </Link>
            ))}
            <div className="pt-3 border-t border-border flex gap-3">
              <Link
                href="/sign-in"
                className="flex-1 py-3 text-center text-sm font-600 border border-border rounded-xl text-foreground hover:bg-background-alt transition-colors"
                style={{ fontWeight: 600 }}
                onClick={() => setMobileOpen(false)}
              >
                Sign In
              </Link>
              <Link
                href="/sign-up"
                className="flex-1 py-3 text-center text-sm font-700 bg-accent text-primary rounded-xl hover:bg-accent-dark transition-colors"
                style={{ fontWeight: 700 }}
                onClick={() => setMobileOpen(false)}
              >
                Sign Up
              </Link>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
